import React, { Suspense } from 'react';

import { Box, GDSBoxProps, GDSStyleObject } from '@leagueplatform/genesis-core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { HEADER_NAV_BAR_DEFAULT_HEIGHT } from '@leagueplatform/solutions-foundation';
import { AppNavBarComponent } from 'components/header-nav/header-nav.component';
import { AppFooter } from 'components/app-footer/app-footer.component';
import { ConnectionErrorPage } from 'pages/errors/connection-error/connection-error.page';
import { QueryParamModal } from 'components/query-param-modal/query-param-modal.component';

const LazyChat = React.lazy(async () => {
  const [{ Chat }, { chatInstance }, { LiveChatCta }] = await Promise.all([
    import('@leagueplatform/chat'),
    import('clients/chat-instance'),
    import('./live-chat-cta.component'),
  ]);
  return {
    default: () => (
      <>
        <Chat chatInstance={chatInstance} />
        <LiveChatCta chatInstance={chatInstance} />
      </>
    ),
  };
});

type BasePageProps = GDSBoxProps<'div'> & {
  css?: GDSStyleObject;
  isPrivatePage?: boolean;
};

export const BasePage: React.FC<BasePageProps> = ({
  children,
  isPrivatePage = true,
  css,
  ...boxProps
}) => {
  useScrollTopOnLocationChange();

  const mainContentMinHeight = isPrivatePage
    ? `calc(100vh - ${HEADER_NAV_BAR_DEFAULT_HEIGHT}px)`
    : '100vh';

  return (
    <>
      {isPrivatePage && <AppNavBarComponent />}
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          zIndex: 1,
          flex: '1',
          minHeight: mainContentMinHeight,
          overflowX: 'hidden',
          paddingBottom: '$two',
          backgroundColor: '$surfaceBackgroundPrimary',
          ...css,
        }}
        {...boxProps}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <ObservabilityErrorBoundary
            fallback={<ConnectionErrorPage />}
            errorContext={{
              context: {
                Context: { location: 'Base Page ErrorBoundary' },
              },
            }}
          >
            {children}
          </ObservabilityErrorBoundary>
        </Suspense>
        {isPrivatePage && (
          <ObservabilityErrorBoundary
            fallback={null}
            errorContext={{
              context: { Context: { location: 'Chat V2 ErrorBoundary' } },
            }}
          >
            <Suspense fallback={null}>
              <LazyChat />
            </Suspense>
          </ObservabilityErrorBoundary>
        )}
        {isPrivatePage && (
          <ObservabilityErrorBoundary
            fallback={null}
            errorContext={{
              context: {
                Context: { location: 'QueryParamModal ErrorBoundary' },
              },
            }}
          >
            <QueryParamModal />
          </ObservabilityErrorBoundary>
        )}
      </Box>
      {isPrivatePage && <AppFooter />}
    </>
  );
};
